import {
  BoardModel,
  CreateBoardOptions,
  BoardConfig,
} from '@models/board-config.interface';
import type BoardView from '@models/board-view';
import { NodeConfig } from '@models/nodes';
import { BoardService } from '@services/game/BoardService';
import { NodeService } from '@services/game/NodeService';
import { DPR } from '@constants/common.const';

export const createBoard = (
  scene: Phaser.Scene,
  boardConfig: BoardConfig,
  boardService: BoardService,
  opts: CreateBoardOptions = {},
): BoardView => {
  const nodeService = new NodeService(); // per-board instance
  const nodeConfig: NodeConfig = {
    width: 64 * DPR,
    height: 40 * DPR,
    checkpointHeight: 128 * DPR,
    checkpointWidth: 128 * DPR,
  };

  const build = (w: number, h: number) => {
    const model: BoardModel = boardService.buildBoard(boardConfig);
    // Dynamically adjust some layout settings based on number of
    // nodes to ensure wave looks good
    // For fewer nodes, increase the gap to stretch the wave pattern
    // TODO: not sure if this is still needed
    //  after adding a new path setting. need to be checked
    // let amplitude = settings.amplitude ?? 200;
    // let waves = settings.waves ?? 1.8;
    // if (model.length <= 15) {
    //   // Scale factor increases as nodes decrease
    //   // 10 nodes: 1.0x, 5 nodes: 2.0x, 3 nodes: 3.3x
    //   const scaleFactor = Math.max(1, 10 / model.length);
    //   gapY = Math.round(gapY * scaleFactor);
    //   amplitude = Math.round(amplitude / scaleFactor);
    //   waves = 1;
    // }
    const layout = boardService.createWaveLayout(model, w, h, {
      gapY: 20 * DPR,
      marginTop: 80 * DPR,
      marginBottom: 100 * DPR,
      settings: boardConfig.settings,
    });

    return { model, layout };
  };

  let { model, layout } = build(scene.scale.width, scene.scale.height);

  scene.cameras.main.setBounds(0, 0, scene.scale.width, layout.worldHeight);

  // Scroll to Start
  scene.cameras.main.scrollY = layout.initialScrollY;

  // Build views
  const root = nodeService.create(scene, layout, nodeConfig);
  // initial state
  let currentPosition = Phaser.Math.Clamp(
    opts.startPosition ?? 0,
    0,
    model.length,
  );

  // Only highlight if we are on the board
  if (currentPosition > 0) {
    nodeService.restorePassedNodes(currentPosition);
  }

  const scrollToIndex = (index: number, instant = false) => {
    const p = nodeService.getNodePosition(index);
    if (!p) return;

    const cam = scene.cameras.main;

    // keep current node around 55% down the screen (feels like moving forward)
    const desiredScreenY = scene.scale.height * 0.55;
    const maxScroll = Math.max(0, cam.getBounds().height - scene.scale.height);

    const targetScrollY = Phaser.Math.Clamp(p.y - desiredScreenY, 0, maxScroll);

    if (instant) {
      cam.scrollY = targetScrollY;
      return;
    }

    scene.tweens.add({
      targets: cam,
      scrollY: targetScrollY,
      duration: 320,
      ease: 'Cubic.easeOut',
    });
  };

  // Reposition camera to current node on initial load (overrides initialScrollY)
  if (currentPosition > 0) {
    scrollToIndex(currentPosition, true);
  }

  const setCurrentPosition = (nextPos: number) => {
    const next = Phaser.Math.Clamp(nextPos, 0, model.length);
    if (next === currentPosition) return;

    const prev = currentPosition;
    currentPosition = next;

    if (prev > 0 && next > 0) {
      // normal move: prev becomes activated, next becomes current
      nodeService.setCurrent(next, prev);
      scrollToIndex(next);
      return;
    }

    if (prev === 0 && next > 0) {
      // first entry onto board: only set current (no passed)
      nodeService.setCurrent(next);
      scrollToIndex(next);
      return;
    }

    if (next === 0) {
      // reset: clear all current effects + (optionally) clear passed states
      nodeService.clearCurrent(); // implement below
      // optional: nodeService.resetAllToNormal();
      return;
    }
  };

  // Resize: rebuild layout + views (keeps currentPosition)
  const rebuildOnResize = opts.rebuildOnResize ?? true;

  const onResize = (gameSize: Phaser.Structs.Size) => {
    if (!rebuildOnResize) return;

    const built = build(gameSize.width, gameSize.height);
    model = built.model;
    layout = built.layout;

    nodeService.create(scene, layout, nodeConfig);
    // Restore all passed nodes and current node after rebuild
    nodeService.restorePassedNodes(currentPosition);

    scene.cameras.main.setBounds(0, 0, gameSize.width, layout.worldHeight);
    scrollToIndex(currentPosition);
  };

  if (rebuildOnResize) {
    scene.scale.on('resize', onResize);
  }

  const destroy = () => {
    if (rebuildOnResize) scene.scale.off('resize', onResize);
    nodeService.destroy();
  };

  const startCurrentNodeOutline = () => {
    if (currentPosition > 0) {
      nodeService.startNodeOutline(currentPosition);
    }
  };

  const flashNode = async (
    nodeIndex: number,
    message: string,
  ): Promise<void> => {
    await nodeService.flashNode(nodeIndex, message);
  };

  const resetNodesToDefault = (from: number, to: number): void => {
    nodeService.resetNodesToDefault(from, to);
  };

  const rewardConfirmedAt = (index: number): void => {
    nodeService.rewardConfirmedAt(index);
  };

  const passThrough = (nodeIndex: number) => {
    nodeService.passThrough(nodeIndex);
  };

  const landOn = (nodeIndex: number) => {
    nodeService.landOn(nodeIndex);
  };

  const refreshPassedNodes = (passedNodes: ReadonlySet<number>) => {
    // Re-apply the passed-through visual state on init/resume
    for (const i of passedNodes) {
      nodeService.passThrough(i);
    }
  };

  return {
    // data
    get model() {
      return model;
    },
    get layout() {
      return layout;
    },

    // view
    root,

    // state API
    getCurrentPosition: () => currentPosition,
    setCurrentPosition,
    scrollToIndex,
    startCurrentNodeOutline,
    flashNode,

    passThrough,
    landOn,
    refreshPassedNodes,
    resetNodesToDefault,
    rewardConfirmedAt,

    destroy,
  };
};
